import ExpandIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Grid, Typography } from '@mui/material';
import { HEMATOXYLIN_STAIN_DISPLAY_NAME, HEMATOXYLIN_STAIN_ID, StainType } from 'interfaces/stainType';
import { CalculationSettingsMultiStain, CalculationSettingsSingleStain, Study } from 'interfaces/study';
import { filter, fromPairs, includes, isEmpty, keys, map, split } from 'lodash';
import React, { useMemo } from 'react';
import { useStudyStainTypes } from 'utils/queryHooks/stainType/useStudyStainTypes';
import { AddStain } from '../AddStain';
import { DeconvolutionStainMapping } from './DeconvolutionStainMapping';

export const defaultDeconvolutionStainVector = [0.27, 0.57, 0.78];
export const defaultDeconvolutionStainVectorHematoxylin = [0.65, 0.7, 0.29];

export const DeconvolutionCalculationSettings: React.FC<
  React.PropsWithChildren<{
    studyId: string;
    updatedSettings: Study['settings'];
    setUpdatedSettings: React.Dispatch<React.SetStateAction<Study['settings']>>;
  }>
> = ({ studyId, updatedSettings, setUpdatedSettings }) => {
  const updatedDeconvolutionSettings = updatedSettings.deconvolutionSettings;
  const updatedCalculationSettings = updatedDeconvolutionSettings?.calculationSettings;

  const { stainTypes } = useStudyStainTypes(studyId);

  const stainTypesWithHematoxylin = [
    ...stainTypes,
    {
      id: HEMATOXYLIN_STAIN_ID,
      displayName: HEMATOXYLIN_STAIN_DISPLAY_NAME,
    } as StainType,
  ];

  const unassignedDeconvolutionStains = useMemo(
    () => filter(stainTypesWithHematoxylin, (stainType) => !updatedCalculationSettings?.[stainType?.id]),
    [stainTypesWithHematoxylin, updatedCalculationSettings]
  );

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandIcon />}>
        <Typography variant="h3">Single component deconvolution stain vectors per stain for quantification</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid
          sx={{
            p: 2,
          }}
          container
          spacing={2}
        >
          <Grid item xs={12} direction="row">
            {map(keys(updatedCalculationSettings), (stainTypeId) => (
              <Grid item xs={12} key={stainTypeId}>
                <DeconvolutionStainMapping
                  stainTypeId={stainTypeId}
                  updatedSettings={updatedSettings}
                  setUpdatedSettings={setUpdatedSettings}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
        {!isEmpty(unassignedDeconvolutionStains) && (
          <AddStain
            unassignedDeconvolutionStains={unassignedDeconvolutionStains}
            setUpdatedSettings={(newStain: string) => {
              const newStainDefault = includes(newStain, '_')
                ? fromPairs(map(split(newStain, '_'), (stain) => [stain, defaultDeconvolutionStainVector]))
                : newStain === HEMATOXYLIN_STAIN_ID
                ? defaultDeconvolutionStainVectorHematoxylin
                : defaultDeconvolutionStainVector;

              setUpdatedSettings({
                ...updatedSettings,
                deconvolutionSettings: {
                  ...updatedDeconvolutionSettings,
                  calculationSettings: {
                    ...updatedCalculationSettings,
                    [newStain]: newStainDefault,
                  } as CalculationSettingsMultiStain | CalculationSettingsSingleStain,
                },
              });
            }}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
};
